import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ReactSVG } from "react-svg";
import printicon from "../../../../assets/images/print-icon.svg";
import editicon from "../../../../assets/images/edit-icon.svg";
import duplicatePlusIcon from "../../../../assets/images/duplicate-plus-icon.svg";
import deleteicon from "../../../../assets/images/delete-icon.svg";
import viewicon from "../../../../assets/images/view-icon.svg";
import { toggleClass } from "../../../../utils/commonUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { DeleteConfirmModel } from "../../Itemlisting/popUpModels/deleteConfModel";
import useInventory from "../../../../Hooks/useInventory";
import { selectCurrentUserId } from "../../../../redux/auth/authSlice";
import { useAppSelector } from "../../../../redux/hooks";
import PrintBarcodeLabelDropdown from "../../../../components/printBarcodeLabelDropdown/printBarcodeLabelDropdown";
import AddItem from "../../AddInventory/AddItem";
import reporticon from "../../../../assets/images/report-icon.svg";
import {
  useModulePermissions,
  PermissionModule,
  PermissionIdentifier,
} from "../../../../utils/permissionUtils";

export const BasicInfoTab = ({
  toggleBarcodeModal,
  selectedItem,
  toggleDuplicateModal,
  selectedLabel,
  setSelectedLabel,
  settingsList,
  useFor,
  setUseFor,
  onShowReportModal,
}: any) => {
  const currentUserId = useAppSelector(selectCurrentUserId);

  const isInventoryPermitted = useModulePermissions(PermissionModule.INVENTORY);
  const isRxPermitted = useModulePermissions(PermissionModule.RX);
  const isPrintBarcodePermitted = isInventoryPermitted(PermissionIdentifier.PRINT_BARCODE);
  const isViewReportPermitted = isInventoryPermitted(PermissionIdentifier.VIEW_INVENTORY_REPORT);
  const isEditPermitted = isRxPermitted(PermissionIdentifier.EDIT_INVENTORY_ITEM);
  const isDuplicatePermitted = isRxPermitted(PermissionIdentifier.DUPLICATE_INVENTORY_ITEM);
  const isDeletePermitted = isRxPermitted(PermissionIdentifier.DELETE_INVENTORY_ITEM);

  const childRef = useRef<{
    printBarcode: (labelId: string, selectedItem: any) => void;
  }>(null);
  const { deleteInventoryItemAPI } = useInventory();
  const [showModal, setShowModal] = useState(false);

  // ✅ Track whether we are currently in edit mode
  const [isEditMode, setIsEditMode] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Toggle between Edit and View mode
  const handleToggleEditView = () => {
    if (isEditMode) {
      // Currently in edit → switch to view
      setIsEditMode(false);
      setUseFor("View");
    } else {
      // Currently in view → switch to edit
      setIsEditMode(true);
      setUseFor("Edit");
    }
  };

  const deleteItem = async () => {
    await deleteInventoryItemAPI({
      id: selectedItem?.id,
      deletedBy: currentUserId,
    });
    onClose();
    navigate("/inventory");
  };

  const onClose = () => {
    toggleClass();
    setShowModal(!showModal);
  };

  const toggleDelModal = () => {
    toggleClass();
    setShowModal(!showModal);
  };

  const handlePrintBarcode = () => {
    if (childRef.current && selectedLabel) {
      childRef.current?.printBarcode(selectedLabel?.value, selectedItem); // Pass the selected label and a barcode ID
    }
  };

  useEffect(() => {
    if (location.pathname === "/edit-inventory") {
      setIsEditMode(true);
      setUseFor("Edit");
    }
  }, []);

  return (
    <div className="invt-overview-content-wrapper">
      <div className="invt-overview-section-title">
        <div className="inventory-actions-box-wrapper">
          {isPrintBarcodePermitted && (
            <>
              <PrintBarcodeLabelDropdown
                setSelectedLabel={setSelectedLabel}
                selectedLabel={selectedLabel}
                ref={childRef} // Pass the ref to the child component
              />
              <div className="print-barcode-btn-box">
                <span
                  className="print-barcode-btn"
                  onClick={handlePrintBarcode}
                >
                  <ReactSVG
                    className="svg-box"
                    wrapper="span"
                    src={printicon}
                  />
                  Print Barcode
                </span>
              </div>
            </>
          )}
          <div className="inventory-actions-box">
            {isEditPermitted && (
              <span
                className={`invt-action-btn ${isEditMode ? "envt-view-btn" : "envt-edit-btn"}`}
                onClick={handleToggleEditView}
              >
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={
                    <Tooltip>{isEditMode ? "Switch to View" : "Switch to Edit"}</Tooltip>
                  }
                  trigger={["hover", "focus"]}
                >
                  <ReactSVG
                    className="svg-box"
                    wrapper="span"
                    src={isEditMode ? viewicon : editicon}
                  />
                </OverlayTrigger>
              </span>
            )}

            {isDuplicatePermitted && (
              <span
                className="invt-action-btn envt-duplicate-btn"
                onClick={toggleDuplicateModal}
              >
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip>Duplicate</Tooltip>}
                  trigger={["hover", "focus"]}
                >
                  <ReactSVG
                    className="svg-box"
                    wrapper="span"
                    src={duplicatePlusIcon}
                  />
                </OverlayTrigger>
              </span>
            )}

            {isDeletePermitted && (
              <span
                className="invt-action-btn envt-delete-btn"
                onClick={toggleDelModal}
              >
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip>Delete</Tooltip>}
                  trigger={["hover", "focus"]}
                >
                  <ReactSVG
                    className="svg-box"
                    wrapper="span"
                    src={deleteicon}
                  />
                </OverlayTrigger>
              </span>
            )}

            {isViewReportPermitted && (
              <span
                className="invt-action-btn envt-transaction-rpt-btn"
                onClick={onShowReportModal}
              >
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip>Transaction Report</Tooltip>}
                  trigger={["hover", "focus"]}
                >
                  <ReactSVG
                    className="svg-box"
                    wrapper="span"
                    src={reporticon}
                  />
                </OverlayTrigger>
              </span>
            )}
          </div>
        </div>
      </div>
      <AddItem isUseFor={useFor} setIsUseFor={setUseFor} setIsEditMode={setIsEditMode} />
      <DeleteConfirmModel
        showModal={showModal}
        onClose={onClose}
        item={selectedItem}
        deleteItem={deleteItem}
      />
    </div>
  );
};